import { Layout, getThemedContent } from '@hcrshubham/core-components';
import { en } from '@hcrshubham/i18n';
import { useTheme } from 'next-themes';
import Link from 'next/link';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Fragment, useEffect, useState } from 'react';

const LandingPage = () => {
  const { theme } = useTheme();
  const [currentTime, setCurrentTime] = useState(new Date());
  const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL || 'https://shubhamsharma.in').replace(/\/$/, '');
  const homeTitle = 'Shubham Kumar Sharma | Full Stack Java Developer';
  const homeDescription =
    'Full Stack Java Developer in India specializing in Java, Spring Boot, SQL, and NoSQL. Explore projects, certifications, resume, and ways to collaborate.';
  const getSocialLabel = (href: string) => {
    if (href.includes('github.com')) return 'Open GitHub profile';
    if (href.includes('linkedin.com')) return 'Open LinkedIn profile';
    if (href.includes('instagram.com')) return 'Open Instagram profile';
    return 'Open social profile';
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString('en-US'); // 11:11:11 AM
  const formattedDate = currentTime.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).replace(/ /g, '-'); // 20-Apr-2025

  const displayText = (
    <>
      <img src="/assets/star1.svg" alt="Star" /> {formattedTime}{' '}
      <img src="/assets/star1.svg" alt="Star" /> {formattedDate}{' '}
    </>
  );

  const personJsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Shubham Kumar Sharma",
    "url": siteUrl,
    "image": `${siteUrl}/assets/dpPhoto.jpg`,
    "jobTitle": "Full Stack Java Developer",
    "sameAs": [
      "https://www.linkedin.com/in/hcrshubham/",
      "https://github.com/hcrshubham/",
      "https://www.instagram.com/hcrshubham/"
    ]
  };

  return (
    <>
      <NextSeo
        title={homeTitle}
        description={homeDescription}
        canonical={`${siteUrl}/`}
        openGraph={{
          type: 'website',
          url: `${siteUrl}/`,
          title: homeTitle,
          description: homeDescription,
          images: [
            {
              url: `${siteUrl}/assets/dpPhoto.jpg`,
              width: 1200,
              height: 630,
              alt: 'Shubham Kumar Sharma',
            },
          ],
          site_name: 'Shubham Kumar Sharma',
        }}
        twitter={{
          handle: '@hcrshubham',
          site: '@hcrshubham',
          cardType: 'summary_large_image',
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(personJsonLd) }}
        />
      </Head>
      <Layout>
      <section className="about-area">
        <div className="container">
          <div className="row">
            <div className="col-md-6" data-aos="zoom-in">
              <div className="about-me-box shadow-box">
                <Link className="overlay-link" href="/credentials" aria-label="View credentials" />
                <img
                  className="bg-img"
                  src="/assets/bg1.png"
                  alt="background"
                />
                <div className="img-box">
                  <img src={en.landing.bio.media} alt="profile" />
                </div>
                <div className="infos">
                  <h4>{en.landing.bio.caption}</h4>
                  <h1>{en.landing.bio.heading}</h1>
                  <p>{en.landing.bio.description}</p>
                  <br />
                  <p>{en.landing.bio.location}</p>
                  <Link href={en.landing.bio.button.link} className="about-btn">
                    <img
                      src={getThemedContent(theme, en.landing.bio.button.icon)}
                      alt="Button"
                    />
                  </Link>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="about-credentials-wrap">
                <div data-aos="zoom-in" className="marquee-wrapper">
                  <div className="banner shadow-box">
                    <div className="marquee">
                      <div className="marquee-content">
                        {Array(20)
                          .fill(0)
                          .map((_, index) => (
                            <Fragment key={index}>{displayText}</Fragment>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="gx-row d-flex gap-24">
                  <div data-aos="zoom-in">
                    <div className="about-crenditials-box info-box shadow-box h-full">
                      <Link className="overlay-link" href="/about" aria-label="Go to about page" />
                      <img
                        src="/assets/bg1.png"
                        alt="background"
                        className="bg-img"
                      />
                      <img
                        src={en.landing.credentials.media}
                        alt="credentials"
                        id="credentialsiconimg"
                      />
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="infos">
                          <h4>{en.landing.credentials.caption}</h4>
                          <h2>{en.landing.credentials.heading}</h2>
                        </div>
                        <Link
                          href={en.landing.credentials.button.link}
                          className="about-btn"
                        >
                          <img
                            src={getThemedContent(
                              theme,
                              en.landing.credentials.button.icon
                            )}
                            alt="button"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div data-aos="zoom-in">
                    <div className="about-project-box info-box shadow-box h-full">
                      <Link className="overlay-link" href="/certificates" aria-label="Go to certificates page" />
                      <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                      <img src={en.landing.cv.media} alt="My Works" id="MyWorksiconimg" />
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="infos">
                          <h4>{en.landing.cv.caption}</h4>
                          <h2>{en.landing.cv.heading}</h2>
                        </div>
                        <Link
                          href={en.landing.cv.button.link}
                          className="about-btn"
                        >
                          <img
                            src={getThemedContent(
                              theme,
                              en.landing.cv.button.icon
                            )}
                            alt="button"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
          <div className="row mt-24">
            <div className="col-md-12">
              <div className="blog-service-profile-wrap d-flex gap-24">
                <div data-aos="zoom-in">
                  <div className="about-blog-box info-box shadow-box h-full">
                    <Link href="/resume" className="overlay-link" aria-label="Go to resume page" />
                    <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                    <img src={en.landing.blog.media} alt="thumbnail" id="Blogiconimg" />
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="infos">
                        <h4>{en.landing.blog.caption}</h4>
                        <h2>{en.landing.blog.heading}</h2>
                      </div>
                      <Link
                        href={en.landing.blog.button.link}
                        className="about-btn"
                      >
                        <img
                          src={getThemedContent(
                            theme,
                            en.landing.blog.button.icon
                          )}
                          alt="button"
                        />
                      </Link>
                    </div>
                  </div>
                </div>

                <div data-aos="zoom-in">
                  <div className="about-profile-box info-box shadow-box h-full">
                    <img src="/assets/bg1.png" alt="BG" className="bg-img" />

                    {Array.from(
                      {
                        length: Math.ceil(
                          en.landing.profiles.profiles.length / 3
                        ),
                      },
                      (_, i) => i * 2
                    ).map((startIndex, index) => (
                      <div
                        className="inner-profile-icons shadow-box"
                        key={index}
                      >
                        {en.landing.profiles.profiles
                          .slice(startIndex, startIndex + 3)
                          .map((item, i) => (
                            <a
                              href={item.link}
                              key={i}
                              target="_blank"
                              rel="noreferrer noopener"
                              aria-label={getSocialLabel(item.link)}
                            >
                              <i className={item.icon} aria-hidden="true" />
                            </a>
                          ))}
                      </div>
                    ))}

                    <div className="d-flex align-items-center justify-content-between">
                      <div className="infos">
                        <h4>{en.landing.profiles.caption}</h4>
                        <h2>{en.landing.profiles.heading}</h2>
                      </div>
                      <Link
                        href={en.landing.profiles.button.link}
                        className="about-btn"
                      >
                        <img
                          src={getThemedContent(
                            theme,
                            en.landing.profiles.button.icon
                          )}
                          alt="Button"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
                <div data-aos="zoom-in" className="flex-1">
                  <div className="about-services-box info-box shadow-box h-full">
                    <Link
                      href={en.landing.offerings.link}
                      className="overlay-link"
                      aria-label="Go to offerings page"
                    />
                    <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                    {/* <img src={en.landing.offerings.media} alt="thumbnail" /> */}
                    <div className="icon-boxes">
                      {en.landing.offerings.icons.map((item, index) => (
                        <i key={index} className={item} aria-hidden="true" />
                      ))}
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="infos">
                        <h4>{en.landing.offerings.caption}</h4>
                        <h2>{en.landing.offerings.heading}</h2>
                      </div>
                      <Link
                        href={en.landing.offerings.button.link}
                        className="about-btn"
                      >
                        <img
                          src={getThemedContent(
                            theme,
                            en.landing.offerings.button.icon
                          )}
                          alt="Button"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row mt-24">
            <div className="col-md-6" data-aos="zoom-in">
              <div className="about-client-box info-box shadow-box">
                <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                <div className="clients d-flex align-items-start gap-24 justify-content-center">
                  {en.landing.facts.quickFacts.map((item, index) => (
                    <div className="client-item" key={index}>
                      <h2>{item.count}</h2>
                      <p
                        dangerouslySetInnerHTML={{
                          __html: item.label,
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-md-6" data-aos="zoom-in">
              <div className="about-contact-box info-box shadow-box">
                <Link className="overlay-link" href="/contact" aria-label="Go to contact page" />
                <img src="/assets/bg1.png" alt="BG" className="bg-img" />
                <img
                  src="/assets/icons/icon2.png"
                  alt="Icon"
                  className="star-icon"
                />
                <h2
                  dangerouslySetInnerHTML={{
                    __html: en.landing.contact.heading,
                  }}
                ></h2>
                <Link
                  href={en.landing.contact.button.link}
                  className="about-btn"
                >
                  <img
                    src={getThemedContent(
                      theme,
                      en.landing.contact.button.icon
                    )}
                    alt="button"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
    </>
  );
};

export default LandingPage;
